import React, { useEffect, useState } from 'react';
import { api } from '../utils/apiClient';
import {
  Users, UserPlus, Pencil, Trash2, X, Loader2,
  AlertTriangle, ShieldCheck, Search, ChevronDown
} from 'lucide-react';

const emptyForm = {
  nombre: '',
  email: '',
  celular: '',
  nickname: '',
  password: '',
  tipo_doc: '',
  num_doc: '',
  rol: '',
  direccion: '',
  departamento: '',
  municipio: ''
};

const ROLES_FALLBACK = ['Abogado', 'Abogado_Seguimiento', 'Administrador', 'Asistente', 'Cliente', 'Contabilidad'];
const TIPOS_DOC = ['CC', 'CE', 'PEP', 'PPT', 'NIT'];

const rolColor = (rol) => {
  const map = {
    Abogado: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    Administrador: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    Asistente: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Contador: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };
  return map[rol] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  'bg-slate-100 dark:bg-[#0f172a] border border-slate-200 dark:border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-800 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [rolesDisponibles, setRolesDisponibles] = useState(ROLES_FALLBACK);

  useEffect(() => {
    fetchUsuarios();
    api.get('/roles')
      .then(data => {
        const nombres = (Array.isArray(data) ? data : []).map(r => r.nombre_rol);
        if (nombres.length > 0) setRolesDisponibles(nombres);
      })
      .catch(() => {});
  }, []);

  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/usuarios');
      setUsuarios(data || []);
    } catch (err) {
      setError(err.message || 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.nombre || !form.nickname || !form.num_doc || !form.rol) {
      setError('Los campos nombre, nickname, num_doc y rol son requeridos');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/usuarios/${editingId}`, form);
      } else {
        await api.post('/usuarios', form);
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      fetchUsuarios();
    } catch (err) {
      setError(err.message || 'Error al guardar usuario');
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setForm({
      nombre: user.nombre || '',
      email: user.email || '',
      celular: user.celular || '',
      nickname: user.nickname || '',
      password: '',
      tipo_doc: user.tipo_doc || '',
      num_doc: user.num_doc || '',
      rol: user.rol || '',
      direccion: user.direccion || '',
      departamento: user.departamento || '',
      municipio: user.municipio || ''
    });
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desea eliminar/desactivar este usuario?')) return;
    try {
      await api.delete(`/usuarios/${id}`);
      fetchUsuarios();
    } catch (err) {
      setError(err.message || 'Error eliminando usuario');
    }
  };

  const filtered = usuarios.filter((u) =>
    [u.nombre, u.email, u.nickname, u.rol]
      .some((v) => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="h-full overflow-hidden bg-slate-50 dark:bg-transparent flex flex-col font-sans transition-colors duration-500">

      {/* Header */}
      <div className="px-6 md:px-10 pt-4 pb-4 bg-white/90 dark:bg-transparent border-b border-slate-200 dark:border-white/5 backdrop-blur-xl z-20 transition-colors duration-500">
        <div className="max-w-[1700px] mx-auto flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-600 blur-2xl opacity-10 dark:opacity-20 animate-pulse" />
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-3 rounded-2xl text-white shadow-2xl relative z-10 border border-white/10">
                <ShieldCheck size={24} strokeWidth={2.5} />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tighter italic">
                Gestión de <span className="text-blue-600 dark:text-blue-500">Usuarios</span>
              </h1>
              <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 font-bold uppercase tracking-[0.4em] text-[8px]">
                <Users size={10} className="text-blue-600" />
                Control de acceso y roles
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:flex-none">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
              <input
                type="text"
                placeholder="Buscar usuario..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-100 dark:bg-[#0f172a] border border-slate-200 dark:border-white/5 rounded-2xl py-2 pl-12 pr-6 font-bold text-slate-800 dark:text-white outline-none w-full lg:w-72 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm placeholder:text-slate-400"
              />
            </div>
            <button
              onClick={() => { setShowForm((v) => !v); if (showForm) handleCancelEdit(); }}
              className="bg-blue-600 text-white px-6 py-2 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-2 shadow-lg hover:scale-105 active:scale-95 transition-all"
            >
              <UserPlus size={14} />
              Nuevo Usuario
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCancelEdit} />
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#0f172a] rounded-3xl border border-slate-200 dark:border-white/5 shadow-2xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">
                {editingId ? '✏️ Editar usuario' : '➕ Crear nuevo usuario'}
              </h2>
              <button onClick={handleCancelEdit} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                <X size={18} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <Field label="Nombre *">
                  <input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Nombre completo" className={inputCls} />
                </Field>
                <Field label="Email">
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="correo@ejemplo.com" className={inputCls} />
                </Field>
                <Field label="Celular">
                  <input name="celular" value={form.celular} onChange={handleChange} placeholder="300 000 0000" className={inputCls} />
                </Field>

                <Field label="Nickname *">
                  <input name="nickname" value={form.nickname} onChange={handleChange} placeholder="usuario123" className={inputCls} />
                </Field>
                <Field label="Password">
                  <input name="password" type="password" value={form.password} onChange={handleChange} placeholder={editingId ? 'Dejar vacío para no cambiar' : 'Contraseña'} className={inputCls} />
                </Field>
                <Field label="Rol *">
                  <div className="relative">
                    <select name="rol" value={form.rol} onChange={handleChange} className={`${inputCls} w-full appearance-none pr-8`}>
                      <option value="">Seleccionar rol...</option>
                      {rolesDisponibles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </Field>

                <Field label="Tipo documento *">
                  <div className="relative">
                    <select name="tipo_doc" value={form.tipo_doc} onChange={handleChange} className={`${inputCls} w-full appearance-none pr-8`}>
                      <option value="">Seleccionar...</option>
                      {TIPOS_DOC.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </Field>
                <Field label="Número documento *">
                  <input name="num_doc" value={form.num_doc} onChange={handleChange} placeholder="1234567890" className={inputCls} />
                </Field>
                <Field label="Dirección">
                  <input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Calle 00 # 00-00" className={inputCls} />
                </Field>

                <Field label="Departamento">
                  <input name="departamento" value={form.departamento} onChange={handleChange} placeholder="Antioquia" className={inputCls} />
                </Field>
                <Field label="Municipio">
                  <input name="municipio" value={form.municipio} onChange={handleChange} placeholder="Medellín" className={inputCls} />
                </Field>
              </div>

              {error && (
                <div className="mt-4 flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm">
                  <AlertTriangle size={16} /> {error}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-600/20"
                >
                  {editingId ? 'Actualizar' : 'Crear usuario'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2.5 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 font-black text-[11px] uppercase tracking-widest rounded-xl transition-all hover:bg-slate-200 dark:hover:bg-white/10 active:scale-95"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8 custom-scrollbar bg-slate-50 dark:bg-transparent">
        <div className="max-w-[1700px] mx-auto space-y-6">

          {/* Tabla */}
          <div className="bg-white dark:bg-transparent rounded-3xl border border-slate-200 dark:border-white/5 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider">
                Usuarios registrados
              </h2>
              <span className="text-[10px] font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20 px-3 py-1 rounded-full uppercase tracking-wider">
                {filtered.length} {filtered.length === 1 ? 'usuario' : 'usuarios'}
              </span>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Loader2 className="animate-spin text-blue-500" size={48} />
                <p className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest animate-pulse">
                  Cargando usuarios...
                </p>
              </div>
            ) : (
              <>
              <div className="md:hidden space-y-3 p-4">
                {filtered.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <Users size={32} className="mx-auto mb-2 opacity-30" />
                    No se encontraron usuarios
                  </div>
                )}
                {filtered.map(u => (
                  <div key={u.id} className="bg-slate-50 dark:bg-white/[0.02] rounded-2xl p-4 border border-slate-100 dark:border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                          {(u.nombre || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-slate-800 dark:text-white">{u.nombre}</p>
                          <p className="text-[10px] text-slate-500">@{u.nickname}</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(u)} className="p-2 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20 active:scale-90"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(u.id)} className="p-2 rounded-xl bg-red-500/10 text-red-400 border border-red-500/20 active:scale-90"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-slate-400">Email:</span> <span className="text-slate-600 dark:text-slate-300">{u.email || '-'}</span></div>
                      <div><span className="text-slate-400">Doc:</span> <span className="text-slate-600 dark:text-slate-300">{u.tipo_doc} {u.num_doc}</span></div>
                    </div>
                    <div className="mt-2">
                      {u.rol && <span className={`text-[10px] font-black px-3 py-1 rounded-full border uppercase tracking-wider ${rolColor(u.rol)}`}>{u.rol}</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-100 dark:border-white/5">
                      {['ID', 'Nombre', 'Email', 'Nickname', 'Doc', 'Rol', 'Acciones'].map((h) => (
                        <th key={h} className="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-5 py-16 text-center text-slate-400 dark:text-slate-600 font-medium">
                          <Users size={32} className="mx-auto mb-2 opacity-30" />
                          No se encontraron usuarios
                        </td>
                      </tr>
                    )}
                    {filtered.map((u) => (
                      <tr
                        key={u.id}
                        className="border-b border-slate-50 dark:border-white/[0.03] hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-5 py-3.5 text-[11px] font-black text-blue-500">#{u.id}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                              {(u.nombre || '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-slate-800 dark:text-white whitespace-nowrap">{u.nombre}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-slate-500 dark:text-slate-400 font-mono text-xs">{u.email || '—'}</td>
                        <td className="px-5 py-3.5 text-slate-600 dark:text-slate-300 font-medium">{u.nickname || '—'}</td>
                        <td className="px-5 py-3.5 text-slate-500 dark:text-slate-400 text-xs">
                          {u.tipo_doc && u.num_doc ? `${u.tipo_doc} ${u.num_doc}` : u.num_doc || '—'}
                        </td>
                        <td className="px-5 py-3.5">
                          {u.rol ? (
                            <span className={`text-[10px] font-black px-3 py-1 rounded-full border uppercase tracking-wider ${rolColor(u.rol)}`}>
                              {u.rol}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(u)}
                              className="p-2 rounded-xl bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 border border-amber-500/20 transition-all active:scale-90"
                              title="Editar"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(u.id)}
                              className="p-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all active:scale-90"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          {!showForm && error && (
            <div className="flex items-center gap-3 text-red-400 bg-red-500/10 border border-red-500/20 rounded-2xl px-5 py-4 text-sm">
              <AlertTriangle size={18} /> {error}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; }
      `}</style>
    </div>
  );
};

export default Usuarios;
